// In-memory write overlay for replay mode.
//
// The demo has no backend, but visitors expect their clicks to "stick" for the
// session: marking a word Known should keep its colour if they navigate away
// and come back. We record status overrides here (keyed by wordId) and patch
// them into recorded GET payloads on the way out. State lives only in memory,
// so a full page refresh resets the demo to its pristine recorded state — which
// is exactly the behaviour we advertise.

const statusByWordId = new Map<number, number>();
let nextSyntheticId = -1;

export const overlay = {
  setWordStatus(wordId: number | string, status: number | string): void {
    const id = Number(wordId);
    const s = Number(status);
    if (Number.isFinite(id) && Number.isFinite(s)) statusByWordId.set(id, s);
  },

  // A brand-new term (clicked from an unknown word) has no real id. We hand back
  // a unique negative id so callers that read `wordId` from the response keep
  // working; the reader updates its own local state optimistically, so the new
  // colour shows immediately without us having to inject it into fixtures.
  createWord(): number {
    const id = nextSyntheticId;
    nextSyntheticId -= 1;
    return id;
  },

  deleteWord(wordId: number | string): void {
    statusByWordId.delete(Number(wordId));
  },

  hasOverrides(): boolean {
    return statusByWordId.size > 0;
  },

  // Recursively patch any `{ wordId, status }` objects found in a payload with
  // the overridden status. Used for /texts/{id} (words array) and
  // /words/language/{id} (flat list) without hard-coding either shape.
  apply<T>(payload: T): T {
    if (!this.hasOverrides()) return payload;
    return patch(payload) as T;
  },
};

function patch(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(patch);
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) out[k] = patch(v);
    const id = out.wordId;
    if (typeof id === 'number' && statusByWordId.has(id)) {
      out.status = statusByWordId.get(id);
    }
    return out;
  }
  return value;
}
